/**
 * React Query Hooks for ADJUDON API
 * 
 * Provides type-safe, cached API calls with loading/error states
 * Automatically falls back to mock data when ENV.USE_MOCK_DATA is enabled
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { mockData } from '../services/mockData';
import { ENV, useMockData } from '../config/env';
import type * as API from '../types/api';
import { identifyUser, resetUser, setOrganization, trackEvent } from '../services/analytics';
import { TokenManager } from '../services/apiClient';
import { useSelectedEntityId, getSelectedEntityId } from '../services/entitySelection';

type LoginActivityParams = { timeRange?: string };
type ScheduledReportPayload = {
  name: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  recipients: string[];
  includeMetrics?: boolean;
  includeTopAgents?: boolean;
  includeAlerts?: boolean;
  includeLowConf?: boolean;
};
type SavedViewFilters = Record<string, unknown>;

// Helper to switch between real API and mock data
const getDataSource = () => (useMockData() ? mockData : api);

// ============================================================================
// QUERY KEYS (for cache management)
// ============================================================================

export const queryKeys = {
  analytics: ['analytics'] as const,
  cpi: ['cpi'] as const,
  agents: ['agents'] as const,
  agent: (id: string) => ['agents', id] as const,
  agentAnalytics: (id: string) => ['agents', id, 'analytics'] as const,
  traces: (params?: API.ListParams) => ['traces', params] as const,
  trace: (id: string) => ['traces', id] as const,
  // Ohne params → ['reviewQueue'] (Basis-Key), damit Mutationen mit
  // queryKeys.reviewQueue() ALLE Param-Varianten prefix-matchen; mit
  // ['reviewQueue', undefined] schlüge partialMatchKey gegen
  // ['reviewQueue', { status }] fehl und Optimistic-Updates liefen ins Leere.
  reviewQueue: (params?: API.FilterParams) =>
    params ? (['reviewQueue', params] as const) : (['reviewQueue'] as const),
  policies: ['policies'] as const,
  alerts: ['alerts'] as const,
  webhooks: ['webhooks'] as const,
  apiKeys: ['apiKeys'] as const,
  team: ['team'] as const,
  billing: ['billing'] as const,
  notifications: ['notifications'] as const,
  organization: ['organization'] as const,
  workspaces: ['workspaces'] as const,
  auditLog: (params?: API.PaginationParams) => ['auditLog', params] as const,
  currentUser: ['currentUser'] as const,
  loginActivity: (params?: LoginActivityParams) => ['loginActivity', params] as const,
  securitySettings: ['securitySettings'] as const,
  consentHistory: ['consentHistory'] as const,
  onboarding: ['onboarding'] as const,
  onboardingDraft: ['onboarding', 'draft'] as const,
  onboardingState: ['onboarding', 'state'] as const,
  onboardingDpaVerification: ['onboarding', 'dpa-verification'] as const,
  legalDocs: ['legal-docs'] as const,
  legalDoc: (doc: string, locale: string) => ['legal-docs', doc, locale] as const,
  anomalies: (params?: { sinceHours?: number; baselineDays?: number }) => ['anomalies', params] as const,
  feedbackAnalytics: (params?: { days?: number }) => ['feedbackAnalytics', params] as const,
  usageCurrent: ['usageCurrent'] as const,
  usageHistory: ['usageHistory'] as const,
  adminStats: ['adminStats'] as const,
  adminOrganizations: (params?: { page?: number; limit?: number; search?: string }) => ['adminOrganizations', params] as const,
  adminUsers: (params?: { page?: number; limit?: number; search?: string }) => ['adminUsers', params] as const,
  scheduledReports: ['scheduledReports'] as const,
  savedViews: (params?: { workspaceId?: string }) => ['savedViews', params] as const,
  compliancePosture: ['compliancePosture'] as const,
  frameworkCoverage: ['frameworkCoverage'] as const,
  bugReports: (params?: { page?: number; limit?: number; status?: API.BugReportStatus; severity?: API.BugReportSeverity }) => ['bugReports', params] as const,
  bugReport: (id: string) => ['bugReports', id] as const,
};

// ============================================================================
// AUTHENTICATION
// ============================================================================

export function useCurrentUser() {
  return useQuery({
    queryKey: queryKeys.currentUser,
    queryFn: () => getDataSource().getCurrentUser(),
    staleTime: 300000, // 5 minutes
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      getDataSource().login(credentials),
    onSuccess: (data) => {
      // Store auth token
      if (data.token) {
        TokenManager.setAccessToken(data.token);
      }
      // Identify user in analytics (#50)
      if (data.user) {
        identifyUser(data.user.id, {
          email: data.user.email,
          name: data.user.name,
          role: data.user.role,
          organizationId: data.user.organizationId,
        });
        if (data.user.organizationId) {
          setOrganization(data.user.organizationId);
        }
      }
      // Invalidate and refetch user data
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => getDataSource().logout(),
    onSuccess: () => {
      // Reset analytics identity before clearing data (#50)
      resetUser();
      // Clear auth token
      localStorage.removeItem('adjudon_token');
      // Clear all cached data
      queryClient.clear();
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (data: { email: string }) => getDataSource().forgotPassword(data.email),
  });
}

// Magic-link mutation — replaces the LoginPage's prior pattern of
// `await import("../services/api")` inside a click handler. Top-level hook
// gives us a stable `isPending` flag for the button + uniform error shape.
export function useSendMagicLink() {
  return useMutation({
    mutationFn: (data: { email: string }) => api.sendMagicLink(data.email),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (data: { token: string; password: string }) =>
      getDataSource().resetPassword(data),
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name?: string; timezone?: string }) => api.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      api.changePassword(data),
    onSuccess: (data) => {
      // CRITICAL: backend revokes `user.sessionRevokedAt = now` AND
      // issues a fresh token in the response. Without persisting the
      // new token, the frontend keeps using the OLD one — which the
      // backend's protect() middleware now treats as revoked → 401 on
      // the next request → user surprise-logged-out from this device
      // after a "successful" password change. Persist + invalidate.
      if (typeof data?.token === 'string' && data.token.length > 0) {
        try {
          localStorage.setItem('adjudon_token', data.token);
        } catch {
          // localStorage write can fail in private-browsing or storage-
          // quota-exceeded scenarios. Non-fatal — the user just sees
          // the surprise-logout we were trying to prevent.
        }
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      emailNotifications?: boolean;
      activationNudges?: boolean;
      loginAlerts?: boolean;
      compactMode?: boolean;
    }) => api.updatePreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useUpdateSessionTimeout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionTimeout: string) => api.updateSessionTimeout(sessionTimeout),
    onSuccess: (data) => {
      // Backend re-issues the JWT with the new `expiresIn`; swap the
      // fresh token into TokenManager so the change takes effect on
      // the very next request rather than waiting until next login.
      // Without this swap, shortening 7d → 1h would have no visible
      // effect until the existing token naturally expired.
      if (data?.token) {
        TokenManager.setAccessToken(data.token);
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useVerify2FA() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { tempToken: string; code: string }) =>
      getDataSource().verify2FA(data),
    onSuccess: (data) => {
      // Store auth token after 2FA verification
      if (data.token) {
        TokenManager.setAccessToken(data.token);
      }
      // Identify user in analytics so Mfa logins behave like normal logins
      if (data.user) {
        identifyUser(data.user.id, {
          email: data.user.email,
          name: data.user.name,
          role: data.user.role,
          organizationId: data.user.organizationId,
        });
        if (data.user.organizationId) {
          setOrganization(data.user.organizationId);
        }
      }
      // Invalidate and refetch user data
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useResend2FA() {
  return useMutation({
    mutationFn: () => getDataSource().resend2FA(),
  });
}

export function useVerifyEmail() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { token: string }) => 
      getDataSource().verifyEmail(data),
    onSuccess: () => {
      // Invalidate and refetch user data
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useResendVerificationEmail() {
  return useMutation({
    // email is optional — post-signup the user has no JWT yet, so they pass
    // their just-submitted email; authenticated callers can omit it and the
    // backend resolves via req.user.
    mutationFn: (email?: string) => getDataSource().resendVerificationEmail(email),
  });
}

export function useRequestSignup() {
  return useMutation({
    mutationFn: (data: { name: string; email: string; organizationName: string; role?: string }) =>
      api.requestSignup(data),
  });
}

// useCompleteOnboarding / useResetOnboarding removed 2026-05-02 — the
// workspace-onboarding wizard was deleted in favour of per-agent
// onboarding at /dashboard/agents/new (see useCompleteAgentOnboarding).

export function useGoogleAuth() {
  return useMutation({
    mutationFn: () => {
      // Redirect to Google OAuth
      window.location.href = `${ENV.API_BASE_URL}/api/auth/google`;
      return Promise.resolve();
    },
  });
}

export function useGithubAuth() {
  return useMutation({
    mutationFn: () => {
      // Redirect to GitHub OAuth
      window.location.href = `${ENV.API_BASE_URL}/api/auth/github`;
      return Promise.resolve();
    },
  });
}

export function useMicrosoftAuth() {
  return useMutation({
    mutationFn: () => {
      // Redirect to Microsoft OAuth
      window.location.href = `${ENV.API_BASE_URL}/api/auth/microsoft`;
      return Promise.resolve();
    },
  });
}

// ============================================================================
// ANALYTICS
// ============================================================================

export function useAnalytics(params?: { dateFrom?: string; dateTo?: string }) {
  return useQuery({
    queryKey: [...queryKeys.analytics, params],
    queryFn: () => getDataSource().getAnalytics(params),
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refresh every minute
  });
}

export function useAnalyticsComparison(period: '7d' | '30d' | '90d' = '30d') {
  return useQuery({
    queryKey: ['analyticsComparison', period],
    queryFn: () => getDataSource().getAnalyticsComparison(period),
    staleTime: 5 * 60 * 1000, // 5 minutes — comparison data doesn't need real-time updates
    refetchInterval: 10 * 60 * 1000,
  });
}

export function useAnomalies(params?: { sinceHours?: number; baselineDays?: number }) {
  return useQuery({
    queryKey: queryKeys.anomalies(params),
    queryFn: () => getDataSource().getAnomalies(params),
    staleTime: 60000, // 1 minute
  });
}

export function useDecisionPatterns(params?: { days?: number; agentId?: string }) {
  return useQuery({
    queryKey: ['decisionPatterns', params],
    queryFn: () => api.getDecisionPatterns(params),
    staleTime: 120000, // 2 minutes
  });
}

export function useFeedbackAnalytics(params?: { days?: number }) {
  return useQuery({
    queryKey: queryKeys.feedbackAnalytics(params),
    queryFn: () => getDataSource().getFeedbackAnalytics(params),
    staleTime: 60000, // 1 minute
  });
}

// Shared with UsagePage's PageHeader staleAfterMs so the "Updated"/"Stale"
// indicator flips at the same instant the cache is considered stale.
export const USAGE_CURRENT_STALE_MS = 30_000;
// History changes slowly (monthly aggregates) — refresh less aggressively.
export const USAGE_HISTORY_STALE_MS = 300_000;

export function useUsageCurrent() {
  return useQuery({
    queryKey: queryKeys.usageCurrent,
    queryFn: () => getDataSource().getUsageCurrent(),
    staleTime: USAGE_CURRENT_STALE_MS,
  });
}

export function useUsageHistory() {
  return useQuery({
    queryKey: queryKeys.usageHistory,
    queryFn: () => getDataSource().getUsageHistory(),
    staleTime: USAGE_HISTORY_STALE_MS,
  });
}

// ============================================================================
// COMPLIANCE POSTURE (Overview compliance card)
// ============================================================================

// Posture changes slowly (a new incident, a clock checkpoint, an ack) but the
// Hero Alert Band must surface fresh data when a clock crosses the 24h /
// 72h thresholds. 60s refetchInterval matches the cadence the user expects
// for a "live" posture indicator without flooding the backend.
export const COMPLIANCE_POSTURE_STALE_MS = 60_000;
export const COMPLIANCE_POSTURE_REFETCH_MS = 60_000;

export function useCompliancePosture() {
  return useQuery({
    queryKey: queryKeys.compliancePosture,
    queryFn: () => getDataSource().getCompliancePosture(),
    staleTime: COMPLIANCE_POSTURE_STALE_MS,
    refetchInterval: COMPLIANCE_POSTURE_REFETCH_MS,
    // Don't retry on 401/403 — the user just doesn't have the feature, the
    // card hides cleanly via the null-data path.
    retry: 1,
  });
}

// Server-authoritative combined readiness for ONE framework (Status tab single
// source). Same 60s live cadence as the posture indicator. Null on absent
// feature/org → the Status tab falls back to client-side derivation.
export function useFrameworkReadiness(framework: string) {
  // Scope to the selected Aufsichtsobjekt (null = consolidated group roll-up).
  const entityId = useSelectedEntityId();
  return useQuery({
    queryKey: ['frameworkReadiness', framework, entityId ?? 'group'] as const,
    queryFn: () => getDataSource().getFrameworkReadiness(framework, entityId),
    staleTime: COMPLIANCE_POSTURE_STALE_MS,
    refetchInterval: COMPLIANCE_POSTURE_REFETCH_MS,
    retry: 1,
  });
}

// Monats-Serien fuer den Board-Bericht (ReadinessSnapshots).
export function useReadinessHistory(months = 12) {
  const entityId = useSelectedEntityId();
  return useQuery({
    queryKey: ['readinessHistory', months, entityId ?? 'group'] as const,
    queryFn: () => getDataSource().getReadinessHistory(months, entityId),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

// Cross-framework cockpit: readiness for ALL canonical hubs in one request
// (posture computed once server-side). Single source for the Compliance-Übersicht.
export function useAllFrameworksReadiness() {
  const entityId = useSelectedEntityId();
  return useQuery({
    queryKey: ['allFrameworksReadiness', entityId ?? 'group'] as const,
    queryFn: () => getDataSource().getAllFrameworksReadiness(entityId),
    staleTime: COMPLIANCE_POSTURE_STALE_MS,
    refetchInterval: COMPLIANCE_POSTURE_REFETCH_MS,
    retry: 1,
  });
}

// The org's regulated entities ("Aufsichtsobjekte") for the entity selector.
export function useEntities() {
  return useQuery({
    queryKey: ['entities'] as const,
    queryFn: () => getDataSource().getEntities(),
    staleTime: 5 * 60_000,
  });
}

// Per-framework coverage signal for the Overview readiness grid. Honest
// derivation from real org data; fails open per-framework on the backend.
export function useFrameworkCoverage() {
  return useQuery({
    queryKey: queryKeys.frameworkCoverage,
    queryFn: () => getDataSource().getFrameworkCoverage(),
    staleTime: COMPLIANCE_POSTURE_STALE_MS,
    retry: 1,
  });
}

// ============================================================================
// CPI
// ============================================================================

// Single source of truth for the CPI refresh cadence. Imported by the
// methodology blurb in CPIDashboard.tsx and the stale-badge threshold so the
// user-facing "refreshes every Xs" claim can't lie.
export const CPI_REFETCH_MS = 60_000;

export function useCPI() {
  return useQuery({
    queryKey: queryKeys.cpi,
    queryFn: () => getDataSource().getCPI(),
    staleTime: CPI_REFETCH_MS, // aligned with refetch cadence
    refetchInterval: CPI_REFETCH_MS,
  });
}

// ============================================================================
// AGENTS
// ============================================================================

export function useAgents() {
  return useQuery({
    queryKey: queryKeys.agents,
    queryFn: () => getDataSource().getAgents(),
    staleTime: 60_000,
    // Background refetch every 60s so the AgentsPage `live` indicator and
    // staleAfterMs threshold reflect actual data movement. Without this the
    // header pulse + "Updated Xs ago" lied — the data only refreshed on
    // window-focus or explicit invalidation. Aligned with CPI/Overview poll
    // cadences so dashboard freshness is uniform across pages.
    refetchInterval: 60_000,
  });
}

export function useAgent(id: string) {
  return useQuery({
    queryKey: queryKeys.agent(id),
    queryFn: () => api.getAgent(id),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useAgentAnalytics(id: string) {
  return useQuery({
    queryKey: queryKeys.agentAnalytics(id),
    queryFn: () => api.getAgentAnalytics(id),
    enabled: !!id,
    staleTime: 60000, // 1 minute
  });
}

// ML-BOM (CycloneDX 1.7) — routed through api (mock-safe). The old
// MlBomInspectionView called the raw apiClient directly, which 500-polled
// on every demo render (same bug class as the ChainIntegrityBanner fix).
export function useAgentMlBom(id: string) {
  return useQuery({
    queryKey: ['agentMlBom', id],
    queryFn: () => api.getAgentMlBom(id),
    enabled: !!id,
    staleTime: 60000,
  });
}

export function useRefreshAgentMlBom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.refreshAgentMlBom(id),
    onSuccess: (_, id) => queryClient.invalidateQueries({ queryKey: ['agentMlBom', id] }),
  });
}

export function useCreateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agent: API.AgentCreateRequest) => api.createAgent(agent),
    onSuccess: (_, variables) => {
      trackEvent('agent_created', { agentName: variables.name });
      queryClient.invalidateQueries({ queryKey: queryKeys.agents });
      // Onboarding draft also has to refetch — when the wizard creates
      // a fresh agent, the draft endpoint should reflect it immediately
      // (and a parallel tab on /agents/new should pick it up). Without
      // this, the old "no draft" response stays cached until staleTime.
      queryClient.invalidateQueries({ queryKey: queryKeys.onboardingDraft });
    },
  });
}

export function useUpdateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<API.Agent> }) =>
      api.updateAgent(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agent(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.agents });
    },
  });
}

export function useRegenerateAgentKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId: string) => api.regenerateAgentKey(agentId),
    onSuccess: (_, agentId) => {
      trackEvent('agent_key_rotated', { agentId });
      queryClient.invalidateQueries({ queryKey: queryKeys.agent(agentId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.agents });
    },
  });
}

// ── Agent Onboarding (AgentOnboardingPage) ──────────────────────────────
// Read-only draft fetch — useQuery is correct here because the call has
// no side effects (no auto-create). Returns { agent: null } when nothing
// is in flight. Short staleTime so a fresh discard/create reflects in
// the UI without needing a manual invalidation; gcTime kept at default
// so the response doesn't outlive the wizard session.
export function useOnboardingDraft() {
  return useQuery({
    queryKey: queryKeys.onboardingDraft,
    queryFn: () => api.getOnboardingDraft(),
    staleTime: 0,
    gcTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useDiscardOnboardingDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => api.discardOnboardingDraft(agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.onboardingDraft });
      queryClient.invalidateQueries({ queryKey: queryKeys.agents });
    },
  });
}

export function useUpdateOnboardingProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: API.UpdateOnboardingProgressRequest }) =>
      api.updateOnboardingProgress(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agent(variables.id) });
    },
  });
}

export function useCompleteAgentOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId: string) => api.completeAgentOnboarding(agentId),
    onSuccess: (_, agentId) => {
      trackEvent('agent_onboarding_completed', { agentId });
      queryClient.invalidateQueries({ queryKey: queryKeys.agent(agentId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.agents });
    },
  });
}

// ─── Phase 1 Workspace Onboarding (SYNTHESIS_2026-05-05) ─────────────────

/**
 * Derived checklist state from the append-only OnboardingEvent log.
 * Polled every 30s while the wizard is open so that mark-as-completed
 * actions reflect within one tick. Override `enabled` with false for
 * pages that don't need it.
 */
export function useOnboardingState(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.onboardingState,
    queryFn: () => api.getOnboardingState(),
    enabled: opts?.enabled !== false,
    staleTime: 5_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useEmitOnboardingEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: API.EmitOnboardingEventRequest) =>
      api.emitOnboardingEvent(payload),
    onSuccess: (_, variables) => {
      trackEvent(`onboarding_${variables.type}`, {
        persona: variables.persona,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.onboardingState });
    },
  });
}

export function useAcceptDpa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: API.AcceptDpaRequest) => api.acceptDpa(payload),
    onSuccess: () => {
      trackEvent('onboarding_dpa_accepted');
      queryClient.invalidateQueries({ queryKey: queryKeys.onboardingState });
      queryClient.invalidateQueries({ queryKey: queryKeys.onboardingDpaVerification });
    },
  });
}

export function useDpaVerification(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.onboardingDpaVerification,
    queryFn: () => api.getDpaVerification(),
    enabled: opts?.enabled !== false,
    staleTime: 60_000,
  });
}

export function useSeedDemoData() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { persona?: string }) => api.seedDemoData(payload),
    onSuccess: () => {
      trackEvent('onboarding_demo_seeded');
      queryClient.invalidateQueries({ queryKey: queryKeys.traces() });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics });
    },
  });
}

export function useLegalDocs(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.legalDocs,
    queryFn: () => api.getLegalDocs(),
    enabled: opts?.enabled !== false,
    staleTime: 5 * 60_000,
  });
}

export function useLegalDoc(doc: string, locale: string, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.legalDoc(doc, locale),
    queryFn: () => api.getLegalDoc(doc, locale),
    enabled: opts?.enabled !== false && Boolean(doc) && Boolean(locale),
    staleTime: 5 * 60_000,
  });
}

export function useUpdateAgentPolicies() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, policies }: { id: string; policies: API.AgentPolicies }) => 
      api.updateAgentPolicies(id, policies),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agent(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.agents });
    },
  });
}

export function useDeleteAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteAgent(id),
    onSuccess: (_, id) => {
      trackEvent('agent_deleted', { agentId: id });
      queryClient.invalidateQueries({ queryKey: queryKeys.agents });
      // Overview's "Active Agents" count + agent-status rollups come
      // from useAnalytics, not useAgents. Without invalidating the
      // analytics cache here the user sees a freshly-deleted agent
      // hanging around on the Overview KPI bar until the next
      // refetchInterval fires. Other agent mutations (create/update)
      // already invalidate analytics; delete had been forgotten.
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics });
    },
  });
}

// ============================================================================
// TRACES
// ============================================================================

export function useTraces(params?: API.ListParams) {
  return useQuery({
    queryKey: queryKeys.traces(params),
    queryFn: () => getDataSource().getTraces(params),
    staleTime: 10000, // 10 seconds
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

export function useTrace(id: string) {
  return useQuery({
    queryKey: queryKeys.trace(id),
    queryFn: () => api.getTrace(id),
    enabled: !!id,
  });
}

export function useExportTraces() {
  return useMutation({
    mutationFn: (params?: API.FilterParams) => api.exportTraces(params),
    onSuccess: () => {
      trackEvent('export_requested', { source: 'traces' });
    },
  });
}

export function useBulkDeleteTraces() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { ids?: string[] } & API.FilterParams) => api.bulkDeleteTraces(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.traces() });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics });
    },
  });
}

export function useBulkUpdateTraceStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, status }: { ids: string[]; status: 'flagged' | 'approved' | 'escalated' | 'pending' | 'success' }) =>
      api.bulkUpdateTraceStatus(ids, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.traces() });
    },
  });
}

// ============================================================================
// REVIEW QUEUE
// ============================================================================

export function useReviewQueue(params?: API.FilterParams) {
  return useQuery({
    queryKey: queryKeys.reviewQueue(params),
    // Always go through `api.getReviewQueue` rather than `getDataSource()`:
    // it already branches on `shouldUseMockData()` internally and uniformly
    // returns the `ReviewQueueResponse` envelope `{ items, total, limit,
    // truncated }` the consumers (and the optimistic-update mutations) rely
    // on. `mockData.getReviewQueue` returns a bare `ReviewItem[]`, so routing
    // through the data-source union would widen the query type to
    // `ReviewItem[] | ReviewQueueResponse`. Runtime behaviour is identical.
    queryFn: () => api.getReviewQueue(params),
    staleTime: 10000,
    refetchInterval: 10000, // Match PageHeader live timestamp interval (10s)
  });
}

export function useReviewDecision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: API.ReviewActionRequest }) =>
      api.reviewDecision(id, action),
    onMutate: async ({ id, action }) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.reviewQueue() });

      // Snapshot all matching review queue caches
      const previousQueries = queryClient.getQueriesData<API.ReviewQueueResponse>({ queryKey: queryKeys.reviewQueue() });

      // Optimistically update every cached review queue list. Cache shape is
      // `{ items, total, limit, truncated }` — only the items array changes
      // status; meta-counts stay stable until the refetch lands.
      const newStatus = action.decision === 'approve' ? 'approved' : 'rejected';
      queryClient.setQueriesData<API.ReviewQueueResponse>(
        { queryKey: queryKeys.reviewQueue() },
        (old) => old
          ? { ...old, items: old.items.map(item => item.id === id ? { ...item, status: newStatus } : item) }
          : old,
      );

      return { previousQueries };
    },
    onSuccess: (_, variables) => {
      const eventMap: Record<string, API.AnalyticsEventName> = {
        approve: 'review_approved',
        reject: 'review_rejected',
      };
      const eventName = eventMap[variables.action.decision] ?? 'review_approved';
      trackEvent(eventName, { traceId: variables.id });
    },
    onError: (_err, _variables, context) => {
      // Rollback only — do NOT toast. The consuming component already
      // catches the mutateAsync rejection and surfaces a context-specific
      // error message (e.g. "Failed to approve action — try again …").
      // Toasting here too produced a double-fire where the user saw both
      // "Action failed — reverted" AND the component's message at once,
      // with disagreeing wording.
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reviewQueue() });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics });
      queryClient.invalidateQueries({ queryKey: queryKeys.traces() });
    },
  });
}

export function useBulkReviewDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, decision, reason }: { ids: string[]; decision: 'approve' | 'reject'; reason?: string }) =>
      api.bulkReviewDecision(ids, decision, reason),
    onMutate: async ({ ids, decision }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.reviewQueue() });
      const previousQueries = queryClient.getQueriesData<API.ReviewQueueResponse>({ queryKey: queryKeys.reviewQueue() });

      const idSet = new Set(ids);
      const newStatus = decision === 'approve' ? 'approved' : 'rejected';
      queryClient.setQueriesData<API.ReviewQueueResponse>(
        { queryKey: queryKeys.reviewQueue() },
        (old) => old
          ? { ...old, items: old.items.map(item => idSet.has(item.id) ? { ...item, status: newStatus } : item) }
          : old,
      );

      return { previousQueries };
    },
    onSuccess: (_, variables) => {
      trackEvent('review_bulk_action', { action: variables.decision, count: variables.ids.length });
    },
    onError: (_err, _variables, context) => {
      // Rollback only — toast handled by consuming component. See
      // useReviewDecision comment for the double-fire rationale.
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reviewQueue() });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics });
      queryClient.invalidateQueries({ queryKey: queryKeys.traces() });
    },
  });
}

export function useAssignReviewItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, assignedTo }: { id: string; assignedTo?: string }) =>
      api.assignReviewItem(id, assignedTo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reviewQueue() });
    },
  });
}

export function useEscalateReviewItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason, escalateTo }: { id: string; reason: string; escalateTo?: string }) =>
      api.escalateReviewItem(id, reason, escalateTo),
    onSuccess: (_, variables) => {
      trackEvent('review_escalated', { traceId: variables.id });
      queryClient.invalidateQueries({ queryKey: queryKeys.reviewQueue() });
    },
  });
}

// ============================================================================
// POLICIES
// ============================================================================

export function usePolicies() {
  return useQuery({
    queryKey: queryKeys.policies,
    queryFn: () => getDataSource().getPolicies(),
    staleTime: 60000,
  });
}

export function useCreatePolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (policy: Omit<API.Policy, 'id' | 'createdAt' | 'matchCount'>) =>
      api.createPolicy(policy),
    onSuccess: (_, variables) => {
      trackEvent('policy_created', { policyName: variables.name });
      queryClient.invalidateQueries({ queryKey: queryKeys.policies });
    },
  });
}

export function useUpdatePolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<API.Policy> }) =>
      api.updatePolicy(id, updates),
    onSuccess: (_, variables) => {
      // Toggle events carry a single `enabled` update; anything else is a full edit
      const keys = Object.keys(variables.updates);
      const isToggle = keys.length === 1 && keys[0] === 'enabled';
      trackEvent(isToggle ? 'policy_toggled' : 'policy_updated', { policyId: variables.id });
      queryClient.invalidateQueries({ queryKey: queryKeys.policies });
    },
  });
}

export function useDeletePolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deletePolicy(id),
    onSuccess: (_, id) => {
      trackEvent('policy_deleted', { policyId: id });
      queryClient.invalidateQueries({ queryKey: queryKeys.policies });
    },
  });
}

export function usePolicyTemplates() {
  return useQuery({
    queryKey: ['policy-templates'],
    queryFn: () => api.getPolicyTemplates(),
    staleTime: 30 * 1000,
  });
}

export function useCreatePolicyFromTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, name }: { templateId: string; name?: string }) =>
      api.createPolicyFromTemplate(templateId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.policies });
    },
  });
}

// ============================================================================
// ALERTS
// ============================================================================

export function useAlerts() {
  return useQuery({
    queryKey: queryKeys.alerts,
    queryFn: () => getDataSource().getAlerts(),
    staleTime: 60000,
  });
}

export function useCreateAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alert: Omit<API.Alert, 'id' | 'createdAt' | 'triggerCount'>) =>
      api.createAlert(alert),
    onSuccess: (_, variables) => {
      trackEvent('alert_created', { action: variables.name });
      queryClient.invalidateQueries({ queryKey: queryKeys.alerts });
    },
  });
}

export function useUpdateAlert() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<API.Alert> }) => 
      api.updateAlert(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.alerts });
    },
  });
}

export function useDeleteAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.alerts });
    },
  });
}

export function useTestAlert() {
  return useMutation({
    mutationFn: (id: string) => api.testAlert(id),
  });
}

export function useCheckCpiAlerts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.checkCpiAlerts(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.alerts });
    },
  });
}

// ============================================================================
// WEBHOOKS
// ============================================================================

export function useWebhooks(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.webhooks,
    queryFn: () => getDataSource().getWebhooks(),
    staleTime: 60000,
    enabled: options?.enabled !== false,
  });
}

export function useApiKeys() {
  return useQuery({
    queryKey: queryKeys.apiKeys,
    queryFn: () => getDataSource().getApiKeys(),
    staleTime: 60000,
  });
}

export function useCreateWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (webhook: API.WebhookCreateRequest) => api.createWebhook(webhook),
    onSuccess: () => {
      trackEvent('webhook_created');
      queryClient.invalidateQueries({ queryKey: queryKeys.webhooks });
    },
  });
}

export function useUpdateWebhook() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<API.Webhook> }) => 
      api.updateWebhook(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.webhooks });
    },
  });
}

export function useDeleteWebhook() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => api.deleteWebhook(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.webhooks });
    },
  });
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: (id: string) => api.testWebhook(id),
  });
}

// ============================================================================
// TEAM
// ============================================================================

export function useTeam() {
  return useQuery({
    queryKey: queryKeys.team,
    queryFn: () => getDataSource().getTeamMembers(),
    staleTime: 60000,
  });
}

export function useInviteTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invite: API.TeamInviteRequest) => api.inviteTeamMember(invite),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team });
      // Without this, the new pending-invite row only appears after the
      // 60s staleTime elapses or a manual refetch — the user clicks
      // "Send Invitation", sees a success toast, and then their newly
      // invited person is invisible in the Pending Invites section until
      // the next staleness window. Mirror useResendInvite/useRevokeTeamInvite.
      queryClient.invalidateQueries({ queryKey: ['teamInvites'] });
    },
  });
}

export function useUpdateTeamMember() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<API.TeamMember> }) => 
      api.updateTeamMember(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team });
    },
  });
}

export function useRemoveTeamMember() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => api.removeTeamMember(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team });
    },
  });
}

// ============================================================================
// BILLING
// ============================================================================

export function useBillingPlans(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['billingPlans'],
    queryFn: () => api.getBillingPlans(),
    staleTime: 3600000, // 1 hour — prices rarely change
    enabled: options.enabled ?? true,
  });
}

export function useBilling() {
  return useQuery({
    queryKey: queryKeys.billing,
    queryFn: () => getDataSource().getBilling(),
    staleTime: 60000, // 1 minute — plan changes should reflect quickly
  });
}

export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: (plan: string) => api.createCheckoutSession(plan),
  });
}

export function useOpenBillingPortal() {
  return useMutation({
    mutationFn: () => api.getBillingPortalUrl(),
    onSuccess: (data) => {
      // Backend contract is `{ url: string }` but a contract drift (empty
      // body, network truncation, mock shape mismatch) could yield
      // `{ url: undefined }` — `window.location.href = undefined` would
      // navigate to literal "undefined" → 404. Guard before redirect.
      // Throw so the caller's `mutateAsync().catch()` surfaces a toast
      // (cat 27 defensive-returns).
      const url = (data as { url?: unknown } | null | undefined)?.url;
      if (typeof url !== "string" || url.length === 0) {
        throw new Error("Billing portal URL missing from response");
      }
      window.location.href = url;
    },
  });
}

// ============================================================================
// NOTIFICATIONS
// ============================================================================

export function useNotifications() {
  return useQuery({
    queryKey: queryKeys.notifications,
    queryFn: () => getDataSource().getNotifications(),
    staleTime: 10000,
    refetchInterval: 30000,
  });
}

export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.markNotificationAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
}

// Marks every unread notification as read in a single request.
// Replaces the page-side N+1 loop (one PATCH per unread).
export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.markAllNotificationsAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
}

// True "Clear all" — DELETEs every notification for the current user.
// Pre-R3 this hook silently called the mark-all-as-read endpoint.
export function useClearAllNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.clearAllNotifications(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
}

export function useBroadcastNotification() {
  return useMutation({
    mutationFn: (data: { message: string; type?: string }) => api.broadcastNotification(data),
  });
}

// ============================================================================
// BUG REPORTS
// ============================================================================

export function useCreateBugReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: API.CreateBugReportInput) => api.createBugReport(data),
    onSuccess: () => {
      // Invalidate any active admin list view so a freshly-submitted report
      // shows up without a manual refresh.
      queryClient.invalidateQueries({ queryKey: ['bugReports'] });
    },
  });
}

export function useBugReports(params?: { page?: number; limit?: number; status?: API.BugReportStatus; severity?: API.BugReportSeverity }) {
  return useQuery({
    queryKey: queryKeys.bugReports(params),
    queryFn: () => api.getBugReports(params),
    staleTime: 30_000,
  });
}

export function useBugReport(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.bugReport(id ?? ''),
    queryFn: () => api.getBugReport(id as string),
    enabled: Boolean(id),
  });
}

export function useUpdateBugReportStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: API.UpdateBugReportStatusInput }) =>
      api.updateBugReportStatus(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['bugReports'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.bugReport(variables.id) });
    },
  });
}

// ============================================================================
// ORGANIZATION
// ============================================================================

export function useOrganization() {
  return useQuery({
    queryKey: queryKeys.organization,
    queryFn: () => api.getOrganization(),
    staleTime: 300000,
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (updates: Partial<API.Organization>) => api.updateOrganization(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization });
    },
  });
}

export function useUploadOrgLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadOrgLogo(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization });
    },
  });
}

export function usePurgeAllTraces() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.purgeAllTraces(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.traces() });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics });
    },
  });
}

export function useDeleteOrganization() {
  return useMutation({
    mutationFn: () => api.deleteOrganization(),
  });
}

// ============================================================================
// WORKSPACES
// ============================================================================

export function useWorkspaces() {
  return useQuery({
    queryKey: queryKeys.workspaces,
    queryFn: () => api.getWorkspaces(),
    staleTime: 60000,
  });
}

export function useCreateWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string }) => api.createWorkspace(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workspaces });
    },
  });
}

export function useUpdateWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string; isActive?: boolean } }) => 
      api.updateWorkspace(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workspaces });
    },
  });
}

export function useDeleteWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteWorkspace(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workspaces });
    },
  });
}

export function useCreateAPIKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, permissions }: { name: string; permissions: string[] }) =>
      api.createAPIKey(name, permissions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys });
    },
  });
}

export function useRevokeAPIKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (keyId: string) => api.revokeAPIKey(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys });
    },
  });
}

// ============================================================================
// AUDIT LOG
// ============================================================================

export function useAuditLog(
  params?: API.PaginationParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.auditLog(params),
    queryFn: () => api.getAuditLog(params),
    staleTime: 60000,
    enabled: options?.enabled ?? true,
  });
}

export function useExportAuditLog() {
  return useMutation({
    mutationFn: (params?: {
      format?: 'csv' | 'pdf';
      action?: string;
      userId?: string;
      workspaceId?: string;
      dateFrom?: string;
      dateTo?: string;
    }) => api.exportAuditLog(params),
    onSuccess: () => {
      trackEvent('export_requested', { source: 'audit_log' });
    },
  });
}

export function useAdminActivity(params?: { limit?: number }) {
  return useQuery({
    queryKey: ['adminActivity', params],
    queryFn: () => api.getAdminActivity(params),
    staleTime: 30000,
  });
}

// ============================================================================
// ENTERPRISE SECURITY & COMPLIANCE
// ============================================================================

export function useLoginActivity(params?: { timeRange?: string }) {
  return useQuery({
    queryKey: queryKeys.loginActivity(params),
    queryFn: () => getDataSource().getLoginActivity(params),
    staleTime: 30000,
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId }: { sessionId: string }) =>
      api.revokeSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.loginActivity() });
    },
  });
}

/**
 * Sign out of every active session (including this one). After success
 * the user's JWT is dead — the only sensible follow-up is to clear local
 * state and redirect to the login page, which the caller does in
 * `onSuccess`. This hook mirrors `useLogout`'s cleanup so the local
 * session state stays consistent with the server.
 */
export function useRevokeAllSessions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.revokeAllSessions(),
    onSuccess: () => {
      resetUser();
      localStorage.removeItem('adjudon_token');
      queryClient.clear();
    },
  });
}

// ─── Self-service Profile hooks ───────────────────────────────────────

export function useConnectedAccounts() {
  return useQuery({
    queryKey: ['connectedAccounts'] as const,
    queryFn: () => api.getConnectedAccounts(),
    staleTime: 60_000,
  });
}

export function useDisconnectAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (provider: string) => api.disconnectAccount(provider),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connectedAccounts'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useRequestEmailChange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ newEmail, currentPassword }: { newEmail: string; currentPassword: string }) =>
      api.requestEmailChange(newEmail, currentPassword),
    onSuccess: () => {
      // currentUser carries `pendingEmailChange` info, refresh so the
      // banner / pending-state UI shows immediately.
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useVerifyEmailChange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => api.verifyEmailChange(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

/**
 * Triggers a browser save-as download of the GDPR Art. 20 data bundle.
 * The mutation returns nothing meaningful — its side effect is the
 * file save. Errors propagate as normal.
 */
export function useExportMyData() {
  return useMutation({
    mutationFn: async () => {
      const blob = await api.exportMyData();
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        const ts = new Date().toISOString().slice(0, 10);
        a.download = `adjudon-data-export-${ts}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        // Defer revoke so the click handler can fire — same pattern
        // used in the trace export flow.
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
      return { downloaded: true } as const;
    },
  });
}

export function useDeleteMyAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ currentPassword }: { currentPassword: string }) =>
      api.deleteMyAccount(currentPassword, 'DELETE'),
    onSuccess: () => {
      // Account is gone — clear all local state. Caller redirects.
      resetUser();
      localStorage.removeItem('adjudon_token');
      queryClient.clear();
    },
  });
}



export function useSecuritySettings() {
  return useQuery({
    queryKey: queryKeys.securitySettings,
    queryFn: () => getDataSource().getSecuritySettings(),
    staleTime: 60000,
  });
}

export function useSetup2FA() {
  return useMutation({
    mutationFn: () => api.setup2FA(),
  });
}

export function useEnable2FA() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ code }: { code: string }) => api.enable2FA(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.securitySettings });
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useDisable2FA() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ password }: { password: string }) => api.disable2FA(password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.securitySettings });
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useGenerateBackupCodes() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => api.generateBackupCodes(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.securitySettings });
    },
  });
}

export function useRemoveTrustedDevice() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ deviceId }: { deviceId: string }) => 
      api.removeTrustedDevice(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.securitySettings });
    },
  });
}

export function useRequestAccountUnlock() {
  return useMutation({
    mutationFn: (email?: string) => api.requestAccountUnlock(email),
  });
}

export function useUpdateConsentPreferences() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ preferences }: { preferences: Record<string, boolean> }) => 
      api.updateConsentPreferences(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.consentHistory });
    },
  });
}

export function useConsentHistory() {
  return useQuery({
    queryKey: queryKeys.consentHistory,
    queryFn: () => getDataSource().getConsentHistory(),
    staleTime: 300000,
  });
}

export function useInitiateSSO() {
  return useMutation({
    mutationFn: ({ email }: { email: string }) => api.initiateSSO(email),
  });
}

// Test Connection — verifies an OIDC discovery doc / SAML IdP metadata
// against the live IdP BEFORE the admin saves. Runs the same checks
// production sign-in would run at first user login, minus token exchange
// (the admin may not have entered a clientSecret / SP keypair yet).
export function useTestSsoConnection() {
  return useMutation({
    mutationFn: (payload: API.SsoTestPayload) => api.testSsoConnection(payload),
  });
}

// SCIM 2.0 token rotation / revocation (WS-5). Both invalidate the
// `organization` query so the SettingsPage reflects the new metadata
// (last4, generatedAt) immediately. The raw token from rotate() is
// surfaced only via the mutation result — never persisted.
export function useRotateScimToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.rotateScimToken(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization });
    },
  });
}

export function useRevokeScimToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.revokeScimToken(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization });
    },
  });
}

// SSO Activity Log (WS-4) — paginated audit-log entries scoped to SSO
// actions. The `enabled` gate keeps the request from firing when the
// SettingsPage is mounted but the SSO Activity panel hasn't been
// expanded yet (governance-tier orgs may have hundreds of entries).
export function useSsoActivity(params: {
  limit?: number;
  enabled?: boolean;
  status?: 'success' | 'failure';
  action?: string;
  provider?: 'oidc' | 'saml';
  q?: string;
  from?: string;
  to?: string;
} = {}) {
  return useQuery({
    queryKey: ['sso-activity', params.limit ?? 50, params.status ?? null, params.action ?? null, params.provider ?? null, params.q ?? null, params.from ?? null, params.to ?? null],
    queryFn: () => api.getSsoActivity({
      limit: params.limit,
      status: params.status,
      action: params.action,
      provider: params.provider,
      q: params.q,
      from: params.from,
      to: params.to,
    }),
    enabled: params.enabled !== false,
    staleTime: 30000,
  });
}

export function useVerifyDomain() {
  return useMutation({
    mutationFn: ({ domain }: { domain: string }) => 
      api.verifyDomain(domain),
  });
}

// ============================================================================
// MAGIC LINK
// ============================================================================

export function useVerifyMagicLink() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ token }: { token: string }) => 
      api.verifyMagicLink(token),
    onSuccess: (data) => {
      if (data.token) {
        TokenManager.setAccessToken(data.token);
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

// ============================================================================
// PASSKEYS / WEBAUTHN
// ============================================================================

export function useListPasskeys() {
  return useQuery({
    queryKey: ['passkeys'],
    queryFn: () => getDataSource().listPasskeys(),
    staleTime: 60000,
  });
}

export function useRegisterPasskey() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ name: _name }: { name: string }) => {
      // Get registration options from server
      const options = await api.getPasskeyRegistrationOptions();
      
      // Create credential using WebAuthn API
      const credential = await navigator.credentials.create({
        publicKey: options as unknown as PublicKeyCredentialCreationOptions,
      });
      
      if (!credential) throw new Error('Passkey creation failed or was cancelled.');
      
      // Verify with server
      return api.verifyPasskeyRegistration(credential);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['passkeys'] });
    },
  });
}

export function useAuthenticatePasskey() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      // Get authentication options from server
      const options = await api.getPasskeyAuthenticationOptions();
      
      // Get credential using WebAuthn API
      const credential = await navigator.credentials.get({
        publicKey: options as unknown as PublicKeyCredentialRequestOptions,
      });
      
      if (!credential) throw new Error('Passkey authentication failed or was cancelled.');
      
      // Verify with server
      return api.verifyPasskeyAuthentication(credential);
    },
    onSuccess: (data) => {
      if (data.token) {
        TokenManager.setAccessToken(data.token);
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useRemovePasskey() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id }: { id: string }) => api.removePasskey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['passkeys'] });
    },
  });
}

export function useRenamePasskey() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => 
      api.renamePasskey(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['passkeys'] });
    },
  });
}

// ============================================================================
// PASSWORD HISTORY
// ============================================================================

// Imperative mutation — never embed a candidate password in a query cache key.
// Call this from form-submit handlers BEFORE the actual change-password mutation
// to surface "password reused" before the server rejects the change.
export function useCheckPasswordHistory() {
  return useMutation({
    mutationFn: (password: string) => getDataSource().checkPasswordHistory(password),
  });
}

// useOnboardingState / useSaveOnboardingStep removed 2026-05-02 — see
// useCompleteOnboarding deletion comment above. Per-agent onboarding
// uses useOnboardingDraft / useUpdateOnboardingProgress.

// NEW: Avatar Upload Hook
export function useUploadAvatar() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (file: File) => api.uploadAvatar(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

// ============================================================================
// SYSTEM HEALTH
// ============================================================================

export function useSystemHealth() {
  return useQuery({
    queryKey: ['systemHealth'] as const,
    queryFn: () => api.getSystemHealth(),
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // poll every minute
  });
}

// ============================================================================
// SUPER ADMIN
// ============================================================================

export function useAdminStats() {
  return useQuery({
    queryKey: queryKeys.adminStats,
    queryFn: () => api.getAdminStats(),
    staleTime: 30000,
  });
}

export function useAdminMRR() {
  return useQuery({
    queryKey: ['adminMRR'],
    queryFn: () => api.getAdminMRR(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useMaintenanceStatus() {
  return useQuery({
    queryKey: ['maintenanceStatus'],
    queryFn: () => api.getMaintenanceStatus(),
    staleTime: 10 * 1000, // 10s — needs to be relatively fresh
    refetchInterval: 30 * 1000,
  });
}

export function useToggleMaintenanceMode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { enable: boolean; message?: string }) => api.toggleMaintenanceMode(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['maintenanceStatus'] }),
  });
}

export function useAdminOrganizations(params?: { page?: number; limit?: number; search?: string }) {
  return useQuery({
    queryKey: queryKeys.adminOrganizations(params),
    queryFn: () => api.getAdminOrganizations(params),
    staleTime: 30000,
  });
}

export function useAdminUsers(params?: { page?: number; limit?: number; search?: string }) {
  return useQuery({
    queryKey: queryKeys.adminUsers(params),
    queryFn: () => api.getAdminUsers(params),
    staleTime: 30000,
  });
}

export function useCustomDomains(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['custom-domains'],
    queryFn: () => api.getCustomDomains(),
    enabled: options?.enabled ?? true,
  });
}

export function useAddCustomDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { domain: string }) => api.addCustomDomain(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-domains'] });
    },
  });
}

export function useVerifyCustomDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (domainId: string) => api.verifyCustomDomain(domainId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-domains'] });
    },
  });
}

export function useDeleteCustomDomain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (domainId: string) => api.deleteCustomDomain(domainId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-domains'] });
    },
  });
}

export function useSetAdminOrganizationStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.setAdminOrganizationStatus(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganizations() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminStats });
    },
  });
}

export function useSetAdminOrganizationPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, plan }: { id: string; plan: string }) =>
      api.setAdminOrganizationPlan(id, plan),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganizations() });
      // Invalidate billing + currentUser so plan gates reflect the change immediately
      queryClient.invalidateQueries({ queryKey: queryKeys.billing });
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
    },
  });
}

export function useDeleteAdminOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteAdminOrganization(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminOrganizations() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminStats });
    },
  });
}

export function useSetAdminUserStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.setAdminUserStatus(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminStats });
    },
  });
}

export function useScheduledReports() {
  return useQuery({
    queryKey: queryKeys.scheduledReports,
    queryFn: () => api.getScheduledReports(),
    staleTime: 30000,
  });
}

export function useCreateScheduledReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ScheduledReportPayload) => api.createScheduledReport(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.scheduledReports }),
  });
}

export function useUpdateScheduledReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, any> }) => api.updateScheduledReport(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.scheduledReports }),
  });
}

export function useDeleteScheduledReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteScheduledReport(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.scheduledReports }),
  });
}

export function useSendScheduledReportNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.sendReportNow(id),
    // Without this, the row's `sentCount` and `lastSentAt` don't update
    // until the 30s staleTime elapses or a manual refetch — user clicks
    // "Send Now", sees a success toast, but the row's metadata stays
    // visually unchanged. Mirror useCreate/Update/DeleteScheduledReport
    // pattern. Same class as TeamManagement v2 R7 (useInviteTeamMember
    // missing `['teamInvites']` invalidation).
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.scheduledReports }),
  });
}

export function useDownloadComplianceReport() {
  return useMutation({
    mutationFn: () => api.downloadComplianceReport(),
  });
}

export function useSavedViews(params?: { workspaceId?: string }) {
  return useQuery({
    queryKey: queryKeys.savedViews(params),
    queryFn: () => api.getSavedViews(params),
    staleTime: 30000,
  });
}

export function useCreateSavedView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; filters: SavedViewFilters; workspaceId?: string }) => api.createSavedView(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.savedViews() }),
  });
}

export function useUpdateSavedView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { name?: string; filters?: SavedViewFilters } }) =>
      api.updateSavedView(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.savedViews() }),
  });
}

export function useDeleteSavedView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteSavedView(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.savedViews() }),
  });
}

// ============================================================================
// POWER FEATURES (SEMANTIC SEARCH & FEEDBACK INGEST)
// ============================================================================

export function useMemorySearch() {
  return useMutation({
    mutationFn: (params: { query: string; minScore?: number; topK?: number }) => 
      api.searchMemory(params.query, params.minScore, params.topK),
  });
}

export function useIngestFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { traceId: string; action: 'approve' | 'override'; correctedDecision?: string; feedbackNotes?: string }) =>
      api.ingestFeedback(data.traceId, data.action, data.correctedDecision, data.feedbackNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.traces()[0]] });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics });
    },
  });
}

// ============================================================================
// API KEY MANAGEMENT
// ============================================================================

export function useUpdateApiKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.updateApiKey(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys });
    },
  });
}

// ============================================================================
// MULTI-ORG
// ============================================================================

export function useUserOrgs() {
  return useQuery({
    queryKey: ['userOrgs'],
    queryFn: () => api.getUserOrgs(),
    staleTime: 300000,
  });
}

export function useSwitchOrg() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orgId: string) => api.switchOrg(orgId),
    onSuccess: (data) => {
      if (data.token) {
        TokenManager.setAccessToken(data.token);
      }
      queryClient.clear();
    },
  });
}

export function useCreateOrg() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; industry?: string; region?: string; billingEmail?: string }) =>
      api.createOrg(data),
    onSuccess: (data) => {
      if (data.token) {
        TokenManager.setAccessToken(data.token);
      }
      queryClient.clear();
    },
  });
}

// ============================================================================
// AUDIT CHAIN VERIFY
// ============================================================================

export function useVerifyAuditChain() {
  return useMutation({
    mutationFn: () => api.verifyAuditChain(),
  });
}

// ============================================================================
// TEAM INVITES
// ============================================================================

export function useTeamInvites(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['teamInvites'],
    queryFn: () => api.getTeamInvites(),
    staleTime: 60000,
    enabled: options?.enabled ?? true,
  });
}

export function useResendInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.resendInvite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamInvites'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.team });
    },
  });
}

export function useRevokeTeamInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.revokeTeamInvite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamInvites'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.team });
    },
  });
}

// ============================================================================
// AGENT COMPARE & VERSIONS
// ============================================================================

export function useAgentVersions(id: string) {
  return useQuery({
    queryKey: ['agentVersions', id],
    queryFn: () => api.getAgentVersions(id),
    enabled: !!id,
    staleTime: 60000,
  });
}

export function useCompareAgents() {
  return useMutation({
    mutationFn: (params: { agentId: string; v1: string; v2: string }) => api.compareAgents(params),
  });
}

// ============================================================================
// IMPERSONATE USER (Super Admin)
// ============================================================================

export function useImpersonateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.impersonateAdminUser(userId),
    onSuccess: (data) => {
      if (data.token) {
        TokenManager.setAccessToken(data.token);
        queryClient.clear();
      }
    },
  });
}

export function useDemoRequests(params?: { status?: string }) {
  return useQuery({
    queryKey: ['demoRequests', params],
    queryFn: () => api.getDemoRequests(params),
    staleTime: 60000,
  });
}

export function useUpdateDemoRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: { status: string; notes?: string } }) =>
      api.updateDemoRequest(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demoRequests'] });
    },
  });
}

// ============================================================================
// TEAM INVITES
// ============================================================================

export function useValidateInviteToken(token: string | null) {
  return useQuery({
    queryKey: ['inviteToken', token],
    queryFn: () => api.validateInviteToken(token!),
    enabled: !!token,
    retry: false,
  });
}

export function useAcceptInvite() {
  return useMutation({
    mutationFn: ({ token, data }: { token: string; data: { name?: string; password: string } }) =>
      api.acceptInvite(token, data),
    onSuccess: (response) => {
      if (response.token) {
        TokenManager.setAccessToken(response.token);
      }
    },
  });
}

// ── Founder Analytics (#50 Sub-Task D) ──────────────────────────────
export function useFounderAnalytics() {
  return useQuery({
    queryKey: ['founderAnalytics'],
    queryFn: () => api.getFounderAnalytics(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ── In-App Nudges (#7 Activation — Block D) ────────────────────────
export function useActiveNudges() {
  return useQuery({
    queryKey: ['nudges', 'active'],
    queryFn: () => api.getActiveNudges(),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
}

// ── Phase 1 — Hash-Chain Audit ──────────────────────────────────────
export function useHashChainStatus() {
  return useQuery({
    queryKey: ['hashChain', 'status'],
    queryFn: () => api.getHashChainStatus(),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

/**
 * Lücken-Bericht (Erfassungs-Abdeckung). 403 = Feature nicht im Plan —
 * Konsumenten (SealStatusStrip) blenden dann aus statt zu retried-hämmern.
 */
export function useHashChainCoverage() {
  return useQuery({
    queryKey: ['hashChain', 'coverage'],
    queryFn: () => api.getHashChainCoverage(),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    retry: (count, err: unknown) => {
      const status = (err as { status?: number; response?: { status?: number } })?.status
        ?? (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) return false;
      return count < 2;
    },
  });
}

export function useHashChainEntries(params?: { limit?: number; cursor?: string }) {
  return useQuery({
    queryKey: ['hashChain', 'entries', params],
    queryFn: () => api.listHashChainEntries(params),
    staleTime: 30 * 1000,
  });
}

export function useHashChainEntry(id: string | null) {
  return useQuery({
    queryKey: ['hashChain', 'entry', id],
    queryFn: () => api.getHashChainEntry(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Look up the chain entry + Merkle proof for a DecisionTrace.
 * 5min staleTime: chain entries are immutable once written.
 */
export function useHashChainEntryByTrace(traceId: string | null) {
  return useQuery({
    queryKey: ['hashChain', 'entryByTrace', traceId],
    queryFn: () => api.getHashChainEntryByTrace(traceId!),
    enabled: !!traceId,
    staleTime: 5 * 60 * 1000,
    retry: (count, err: any) => {
      // 404 = entry not found yet (trace ingested but chain write pending) — don't retry.
      const status = err?.status ?? err?.response?.status;
      if (status === 404) return false;
      return count < 2;
    },
  });
}

export function useMerkleAnchors(params?: { limit?: number }) {
  return useQuery({
    queryKey: ['hashChain', 'anchors', params],
    queryFn: () => api.listMerkleAnchors(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function useMerkleProof(anchorId: string | null, sequence: number | null) {
  return useQuery({
    queryKey: ['hashChain', 'proof', anchorId, sequence],
    queryFn: () => api.getMerkleProof(anchorId!, sequence!),
    enabled: !!anchorId && sequence !== null,
    staleTime: 5 * 60 * 1000,
  });
}

export function useVerifyHashChain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.verifyHashChain(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hashChain', 'status'] });
    },
  });
}

export function useExportHashChainBundle() {
  return useMutation({
    mutationFn: (params: { fromSequence?: number; toSequence?: number; format?: 'json' | 'jsonl' }) =>
      api.exportHashChainBundle(params),
  });
}

// ── Phase 1 — Compliance Mapping ────────────────────────────────────
export function useComplianceMapping(framework: string = 'iso-42001') {
  return useQuery({
    queryKey: ['compliance', 'mapping', framework],
    queryFn: () => api.getComplianceMapping(framework),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateComplianceMappingNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { clauseId: string; status: import('../types/api').Iso42001ClauseStatus; evidence: string; framework?: string }) =>
      api.updateComplianceMappingNote(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance', 'mapping'] });
    },
  });
}

// ── Phase 2 — Incidents (Multi-Clock) ───────────────────────────────
export function useIncidents() {
  return useQuery({
    queryKey: ['incidents'],
    queryFn: () => api.listIncidents(),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useIncident(id: string | null) {
  return useQuery({
    queryKey: ['incident', id],
    queryFn: () => api.getIncident(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
}

// Council F-43 — DPA registry for GDPR Art. 33 country-selector dropdown.
// Static reference data — 5min stale window is generous.
export function useDpaRegistry() {
  return useQuery({
    queryKey: ['dpa-registry'],
    queryFn: () => api.listDpaRegistry(),
    staleTime: 5 * 60 * 1000,
  });
}

// Council F-26 — Regulator-replay artifact per trace. Auditor power-tool;
// fetched on-demand only (no auto-refetch — the bundle is deterministic
// for a given trace+calibration-map combination).
export function useRegulatorReplay(traceId: string | null) {
  return useQuery({
    queryKey: ['regulator-replay', traceId],
    queryFn: () => api.getRegulatorReplay(traceId!),
    enabled: !!traceId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

// Council F-16 — TEE attestation provider status. Custom-plan-gated.
// 60s refetch so the dashboard reflects provider rotation + freshly verified attestations.
export function useTeeAttestationStatus() {
  return useQuery({
    queryKey: ['tee-attestation-status'],
    queryFn: () => api.getTeeAttestationStatus(),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    retry: false,
  });
}

// ─── Compliance-Maturity Registry (Council F-46 Sprint-Zero) ───────────────
// Backend metadata changes infrequently (only when Adjudon ships new compliance
// claims). 60s stale window keeps the admin matrix snappy without thrashing.
export function useComplianceMaturityEntries(params?: { family?: string; status?: string; includeArchived?: boolean }) {
  return useQuery({
    queryKey: ['compliance-maturity', 'entries', params],
    queryFn: () => api.listComplianceMaturity(params),
    staleTime: 60 * 1000,
  });
}

export function useComplianceMaturitySummary() {
  return useQuery({
    queryKey: ['compliance-maturity', 'summary'],
    queryFn: () => api.getComplianceMaturitySummary(),
    staleTime: 60 * 1000,
  });
}

// ── DORA-Hub — Art. 8 / 17–19 (2026-06-10) ──────────────────────────

/** Art. 8 — ICT-Asset-Register der KI-Schicht. Nicht plan-gated. */
export function useIctAssetRegister() {
  return useQuery({
    queryKey: ['dora', 'ictAssetRegister'],
    queryFn: () => api.getIctAssetRegister(),
    staleTime: 60 * 1000,
  });
}

/** Art. 18 — stateless Klassifizierungs-Rechner (kein Cache-Invalidate noetig). */
export function useClassifyDoraIncident() {
  return useMutation({
    mutationFn: (criteria: import('../types/api').DoraClassificationInput) => api.classifyDoraIncident(criteria),
  });
}

/** Art. 17(2)/18(2) — Cyber-Bedrohungs-Register. 403 = Plan-Gate. */
export function useCyberThreats(params?: { status?: string }) {
  return useQuery({
    queryKey: ['dora', 'cyberThreats', params],
    queryFn: () => api.listCyberThreats(params),
    staleTime: 30 * 1000,
    retry: (count, err: unknown) => {
      const status = (err as { status?: number })?.status ?? (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) return false;
      return count < 2;
    },
  });
}

export function useCreateCyberThreat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { title: string; summary?: string; source?: string; detectedAt?: string }) =>
      api.createCyberThreat(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dora', 'cyberThreats'] }),
  });
}

export function useAssessCyberThreat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: {
      id: string;
      payload: { couldAffectCriticalFunctions: boolean; highProbabilityOfMaterialisation: boolean; couldMeetMaterialityThresholds: boolean; decision?: 'significant' | 'not-significant' };
    }) => api.assessCyberThreat(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dora', 'cyberThreats'] }),
  });
}

export function useUpdateCyberThreat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: {
      id: string;
      payload: { title?: string; summary?: string; source?: string; status?: import('../types/api').CyberThreatStatus; voluntaryNotification?: { decided: boolean; notifiedAt?: string | null; reference?: string | null } };
    }) => api.updateCyberThreat(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dora', 'cyberThreats'] }),
  });
}

// ── GwG/AML-Hub — § 5 / § 6 / §§ 10–15 (2026-06-10) ─────────────────

/** § 5 — aktuelle Risiko-Analyse. 404 = noch keine Version erfasst (kein Retry). */
export function useCurrentRiskAssessment() {
  return useQuery({
    queryKey: ['gwg', 'riskAssessment', 'current'],
    queryFn: () => api.getCurrentRiskAssessment(),
    staleTime: 60 * 1000,
    retry: (count, err: unknown) => {
      const status = (err as { status?: number })?.status ?? (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 403) return false;
      return count < 2;
    },
  });
}

export function useRiskAssessmentVersions() {
  return useQuery({
    queryKey: ['gwg', 'riskAssessment', 'versions'],
    queryFn: () => api.listRiskAssessmentVersions(),
    staleTime: 60 * 1000,
  });
}

/** § 6 — Alert-Triage-Register. */
export function useTransactionAlerts(params?: { status?: string; severity?: string }) {
  return useQuery({
    queryKey: ['gwg', 'transactionAlerts', params],
    queryFn: () => api.listTransactionAlerts(params),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useTransitionTransactionAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: {
      id: string;
      payload: { status: import('../types/api').TransactionAlertStatus; disposition?: import('../types/api').TransactionAlertDisposition; assignedToRef?: string };
    }) => api.transitionTransactionAlert(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gwg', 'transactionAlerts'] }),
  });
}

export function useDisposeTransactionAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, disposition }: { id: string; disposition: import('../types/api').TransactionAlertDisposition }) =>
      api.disposeTransactionAlert(id, disposition),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gwg', 'transactionAlerts'] }),
  });
}

/** §§ 10–15 — Evidenz-Register (read-fokussiert; Erfassung via SDK/API). */
export function useEddAssessments(params?: { status?: string }) {
  return useQuery({
    queryKey: ['gwg', 'edd', params],
    queryFn: () => api.listEddAssessments(params),
    staleTime: 30 * 1000,
  });
}

export function useScreenings(params?: { verdict?: string; status?: string }) {
  return useQuery({
    queryKey: ['gwg', 'screenings', params],
    queryFn: () => api.listScreenings(params),
    staleTime: 30 * 1000,
  });
}

export function useAmlBeneficiaries(params?: { status?: string }) {
  return useQuery({
    queryKey: ['gwg', 'beneficiaries', params],
    queryFn: () => api.listAmlBeneficiaries(params),
    staleTime: 30 * 1000,
  });
}

/** Solvency II Säule 1 — KI-Komponenten-Evidenz (Layer B). 403 = Plan-Gate. */
export function useSolvencyPillar1Evidence(windowDays?: number) {
  return useQuery({
    queryKey: ['solvency', 'pillar1', windowDays],
    queryFn: () => api.getSolvencyPillar1Evidence(windowDays ? { windowDays } : undefined),
    staleTime: 60 * 1000,
    retry: (count, err: unknown) => {
      const status = (err as { status?: number })?.status ?? (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) return false;
      return count < 2;
    },
  });
}

// ── VVG/IDD-Hub — § 60 / IDD Art. 19 / Wiedervorlage (2026-06-11) ────

export function useProductComparisons(params?: { status?: string; advisoryRef?: string }) {
  return useQuery({
    queryKey: ['vvg', 'comparisons', params],
    queryFn: () => api.listProductComparisons(params),
    staleTime: 30 * 1000,
    retry: (count, err: unknown) => {
      const status = (err as { status?: number })?.status ?? (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) return false;
      return count < 2;
    },
  });
}

export function useCreateProductComparison() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<import('../types/api').ProductComparison>) => api.createProductComparison(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vvg', 'comparisons'] }),
  });
}

export function useFinalizeProductComparison() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.finalizeProductComparison(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vvg', 'comparisons'] }),
  });
}

export function useCommissionDisclosures(params?: { status?: string }) {
  return useQuery({
    queryKey: ['vvg', 'commissions', params],
    queryFn: () => api.listCommissionDisclosures(params),
    staleTime: 30 * 1000,
  });
}

export function useCreateCommissionDisclosure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<import('../types/api').CommissionDisclosure>) => api.createCommissionDisclosure(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vvg', 'commissions'] }),
  });
}

export function useDiscloseCommissionDisclosure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.discloseCommissionDisclosure(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vvg', 'commissions'] }),
  });
}

export function useAcknowledgeCommissionDisclosure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.acknowledgeCommissionDisclosure(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vvg', 'commissions'] }),
  });
}

export function useArchiveCommissionDisclosure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.archiveCommissionDisclosure(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vvg', 'commissions'] }),
  });
}

export function usePeriodicReviews(params?: { status?: string }) {
  return useQuery({
    queryKey: ['vvg', 'periodicReviews', params],
    queryFn: () => api.listPeriodicReviews(params),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useCreatePeriodicReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { advisoryRef: string; customerRef?: string; nextReviewDue?: string; reviewReason?: string }) =>
      api.createPeriodicReview(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vvg', 'periodicReviews'] }),
  });
}

export function useCompletePeriodicReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, whatChanged }: { id: string; whatChanged: string }) =>
      api.completePeriodicReview(id, { whatChanged }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vvg', 'periodicReviews'] }),
  });
}

export function useReschedulePeriodicReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, nextReviewDue }: { id: string; nextReviewDue: string }) =>
      api.reschedulePeriodicReview(id, { nextReviewDue }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vvg', 'periodicReviews'] }),
  });
}

// ── EIOPA-Model-Cards (2026-06-11) ───────────────────────────────────

/** Aktuelle Model-Card. 404 = noch keine erfasst (kein Retry, ehrlicher Empty). */
export function useCurrentModelCard(agentId: string | null) {
  return useQuery({
    queryKey: ['modelCard', 'current', agentId],
    queryFn: () => api.getCurrentModelCard(agentId!),
    enabled: !!agentId,
    staleTime: 60 * 1000,
    retry: (count, err: unknown) => {
      const status = (err as { status?: number })?.status ?? (err as { response?: { status?: number } })?.response?.status;
      const code = (err as { code?: string })?.code;
      if (status === 404 || status === 403 || code === 'MODELCARD_NOT_FOUND') return false;
      return count < 2;
    },
  });
}

export function useModelCardVersions(agentId: string | null) {
  return useQuery({
    queryKey: ['modelCard', 'versions', agentId],
    queryFn: () => api.listModelCardVersions(agentId!),
    enabled: !!agentId,
    staleTime: 60 * 1000,
  });
}

export function useCreateModelCardVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, payload }: { agentId: string; payload: { fields: Partial<Record<import('../types/api').ModelCardFieldKey, string>>; changeSummary?: string } }) =>
      api.createModelCardVersion(agentId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['modelCard'] }),
  });
}

export function useCreateIncident() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<import('../types/api').Incident>) => api.createIncident(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['incidents'] }),
  });
}

export function useRecordRegulatorSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, code, payload }: { incidentId: string; code: string; payload: { submittedAt?: string | null; submissionRef?: string; authorityCaseId?: string } }) =>
      api.recordIncidentRegulatorSubmission(incidentId, code, payload),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      queryClient.invalidateQueries({ queryKey: ['incident', vars.incidentId] });
    },
  });
}

export function useUpdateIncident() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<import('../types/api').Incident> }) =>
      api.updateIncident(id, payload),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      queryClient.invalidateQueries({ queryKey: ['incident', vars.id] });
    },
  });
}

export function useCloseIncident() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { rootCause: string; remediation: string } }) =>
      api.closeIncident(id, payload),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      queryClient.invalidateQueries({ queryKey: ['incident', vars.id] });
    },
  });
}

export function useCompleteIncidentCheckpoint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ incidentId, checkpointId, payload }: {
      incidentId: string;
      checkpointId: string;
      payload: { evidenceUrl?: string; notes?: string };
    }) => api.completeIncidentCheckpoint(incidentId, checkpointId, payload),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['incident', vars.incidentId] });
    },
  });
}

// ── Phase 2 — Auto-Approval Engine ──────────────────────────────────
export function useApprovalPatterns() {
  return useQuery({
    queryKey: ['approvalPatterns'],
    queryFn: () => api.listApprovalPatterns(),
    staleTime: 60 * 1000,
  });
}

export function useApprovalPattern(id: string | null) {
  return useQuery({
    queryKey: ['approvalPattern', id],
    queryFn: () => api.getApprovalPattern(id!),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useCreateApprovalPattern() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<import('../types/api').ApprovalPattern>) => api.createApprovalPattern(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['approvalPatterns'] }),
  });
}

export function usePauseApprovalPattern() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.pauseApprovalPattern(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['approvalPatterns'] }),
  });
}

export function useSignoffApprovalPattern() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reviewerRole }: { id: string; reviewerRole: 'primary' | 'secondary' }) =>
      api.signoffApprovalPattern(id, { reviewerRole }),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['approvalPatterns'] });
      queryClient.invalidateQueries({ queryKey: ['approvalPattern', vars.id] });
    },
  });
}

export function useAutoApprovalDecisions(params?: { patternId?: string; limit?: number }) {
  return useQuery({
    queryKey: ['autoApprovalDecisions', params],
    queryFn: () => api.listAutoApprovalDecisions(params),
    staleTime: 30 * 1000,
  });
}

// ── Phase 2 — Deployer Profile ──────────────────────────────────────
export function useDeployerProfile() {
  return useQuery({
    queryKey: ['deployerProfile'],
    queryFn: () => api.getDeployerProfile(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateDeployerProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<import('../types/api').DeployerProfile>) => api.updateDeployerProfile(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deployerProfile'] }),
  });
}

export function useIngestDeployerInstruction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { title: string; body: string; source?: 'provider' | 'internal' }) =>
      api.ingestDeployerInstruction(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deployerProfile'] }),
  });
}

export function useAcknowledgeDeployerInstruction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.acknowledgeDeployerInstruction(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deployerProfile'] }),
  });
}

export function useLinkFriaToDeployer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (friaId: string) => api.linkFriaToDeployer(friaId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deployerProfile'] }),
  });
}

// ── Controller-Responsibility catalog (⚪ non-delegable duties) ──────
// Per-entity (Aufsichtsobjekt): the apiClient sends the selected entity as
// X-Entity-ID, so the backend scopes attestations; the id in the queryKey makes
// a switch refetch.
export function useControllerResponsibilities(framework?: string) {
  const entityId = useSelectedEntityId();
  return useQuery({
    queryKey: ['controllerResponsibilities', framework ?? 'all', entityId ?? 'default'],
    queryFn: () => api.getControllerResponsibilities(framework),
    staleTime: 60 * 1000,
  });
}

export function useAttestResponsibility(_framework?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // Pin the selected Aufsichtsobjekt at CALL time (not render time) into the
    // request body — the backend prefers it over the X-Entity-ID header, closing
    // the entity-switch race where the header could change mid-flight.
    mutationFn: (vars: { key: string; note?: string; evidenceUrl?: string; performedAt?: string; attestedByFunction?: string }) =>
      api.attestResponsibility(vars.key, {
        note: vars.note,
        evidenceUrl: vars.evidenceUrl,
        performedAt: vars.performedAt,
        attestedByFunction: vars.attestedByFunction,
        entityId: getSelectedEntityId(),
      }),
    onSuccess: () => {
      // Prefix-invalidate every scope/framework so the active list refetches
      // regardless of which entity is selected now.
      queryClient.invalidateQueries({ queryKey: ['controllerResponsibilities'] });
      queryClient.invalidateQueries({ queryKey: ['frameworkReadiness'] });
    },
  });
}

export function useRevokeResponsibility(_framework?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => api.revokeResponsibility(key, getSelectedEntityId()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controllerResponsibilities'] });
      queryClient.invalidateQueries({ queryKey: ['frameworkReadiness'] });
    },
  });
}

// Immutable attestation history for one obligation (lazy — only when opened).
export function useResponsibilityHistory(key: string, enabled: boolean) {
  return useQuery({
    queryKey: ['responsibilityHistory', key, useSelectedEntityId() ?? 'default'] as const,
    queryFn: () => getDataSource().getResponsibilityHistory(key),
    enabled,
    staleTime: 30_000,
  });
}

// ── GwGMeldV / goAML-XML Verdachtsmeldung ───────────────────────────────────
export function useGoAmlProfile() {
  return useQuery({
    queryKey: ['goamlProfile'],
    queryFn: () => api.getGoAmlProfile(),
    staleTime: 60 * 1000,
  });
}

export function useUpdateGoAmlProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<API.GoAmlReportingProfile>) => api.updateGoAmlProfile(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['goamlProfile'] }),
  });
}

// ── Phase 2 — FRIA ──────────────────────────────────────────────────
export function useFRIAs() {
  return useQuery({
    queryKey: ['fria', 'list'],
    queryFn: () => api.listFRIAs(),
    staleTime: 60 * 1000,
  });
}

// EU AI Act Art. 26/27 advisory FRIA-coverage (which KI-Systeme are FRIA-approved).
export function useFriaCoverage() {
  return useQuery({
    queryKey: ['fria', 'coverage'],
    queryFn: () => api.getFriaCoverage(),
    staleTime: 60 * 1000,
    retry: 1,
  });
}

export function useFRIA(id: string | null) {
  return useQuery({
    queryKey: ['fria', 'detail', id],
    queryFn: () => api.getFRIA(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

export function useCreateFRIA() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<import('../types/api').FRIA>) => api.createFRIA(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fria'] }),
  });
}

export function useUpdateFRIA() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<import('../types/api').FRIA> }) =>
      api.updateFRIA(id, payload),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['fria'] });
      queryClient.invalidateQueries({ queryKey: ['fria', 'detail', vars.id] });
    },
  });
}

export function useSubmitFRIA() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.submitFRIAForReview(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fria'] }),
  });
}

// Art. 27 — generic transition (e.g. review -> draft = Zurueckweisung an Bearbeiter).
export function useTransitionFRIA() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api.transitionFRIA(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fria'] }),
  });
}

export function useApproveFRIA() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.approveFRIA(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fria'] }),
  });
}

// EU AI Act Art. 27 — pre-filled insurance FRIA template (Annex III §5(b)/(c)).
// Button-triggered, so modelled as a mutation rather than a standing query.
export function useInsuranceFriaTemplate() {
  return useMutation({
    mutationFn: (useCase?: string) => api.getInsuranceFriaTemplate(useCase),
  });
}

// EU AI Act Art. 72(3) — post-market corrective actions (remediation tracking).
export function useCorrectiveActions(status?: string) {
  return useQuery({
    queryKey: ['correctiveActions', status ?? 'all'] as const,
    queryFn: () => api.getCorrectiveActions(status),
    staleTime: 60 * 1000,
  });
}
export function useCreateCorrectiveAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { title: string; source?: string; description?: string; agentId?: string; dueDate?: string }) => api.createCorrectiveAction(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['correctiveActions'] }),
  });
}
export function useUpdateCorrectiveAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { status?: string } }) => api.updateCorrectiveAction(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['correctiveActions'] }),
  });
}

// EU AI Act Art. 50(2) — set a KI-System's outputType (drives the auto-watermark trigger).
export function useUpdateAgentModelMetadata() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<import('../types/api').AgentModelMetadata> }) => api.updateAgentModelMetadata(id, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agents'] }),
  });
}

// EU AI Act Art. 27 — signed JSON export (verifiable ES256 JWS / SHA-256 fallback).
export function useExportFRIA() {
  return useMutation({
    mutationFn: (id: string) => api.exportFRIA(id),
  });
}

// EU AI Act Art. 27 — signed PDF export (binary Blob).
export function useExportFRIAPdf() {
  return useMutation({
    mutationFn: (id: string) => api.exportFRIAPdf(id),
  });
}

// ── VVG §61/§62 — Advisory Records ──────────────────────────────────
export function useAdvisories(params?: { status?: string; search?: string; customerRef?: string; limit?: number }) {
  return useQuery({
    queryKey: ['advisory', 'list', params?.status ?? 'all', params?.search ?? '', params?.customerRef ?? ''],
    queryFn: () => api.listAdvisories(params),
    staleTime: 60 * 1000,
  });
}

export function useAdvisory(id: string | null) {
  return useQuery({
    queryKey: ['advisory', 'detail', id],
    queryFn: () => api.getAdvisory(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

export function useCreateAdvisory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<import('../types/api').AdvisoryRecord>) => api.createAdvisory(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['advisory'] }),
  });
}

export function useUpdateAdvisory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<import('../types/api').AdvisoryRecord> }) =>
      api.updateAdvisory(id, payload),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['advisory'] });
      queryClient.invalidateQueries({ queryKey: ['advisory', 'detail', vars.id] });
    },
  });
}

export function useFinalizeAdvisory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: { advisedAt?: string } }) =>
      api.finalizeAdvisory(id, payload),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['advisory'] });
      queryClient.invalidateQueries({ queryKey: ['advisory', 'detail', vars.id] });
    },
  });
}

export function useAcknowledgeAdvisory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { method: import('../types/api').AdvisoryAcknowledgmentMethod; acknowledgedByRef: string; statement: string } }) =>
      api.acknowledgeAdvisory(id, payload),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['advisory'] });
      queryClient.invalidateQueries({ queryKey: ['advisory', 'detail', vars.id] });
    },
  });
}

export function useArchiveAdvisory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.archiveAdvisory(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['advisory'] }),
  });
}

export function useAdvisoryProtocol(id: string | null) {
  return useQuery({
    queryKey: ['advisory', 'protocol', id],
    queryFn: () => api.getAdvisoryProtocol(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useDeleteAdvisory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteAdvisory(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['advisory'] }),
  });
}

// ── Phase 3 — Federated Learning ────────────────────────────────────
export function usePatternFamilies() {
  return useQuery({
    queryKey: ['federated', 'families'],
    queryFn: () => api.listPatternFamilies(),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePatternFamily(id: string | null) {
  return useQuery({
    queryKey: ['federated', 'family', id],
    queryFn: () => api.getPatternFamily(id!),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useJoinPatternFamily() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.joinPatternFamily(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['federated'] }),
  });
}

export function useLeavePatternFamily() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.leavePatternFamily(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['federated'] }),
  });
}

export function useFederatedRounds(familyId: string | null) {
  return useQuery({
    queryKey: ['federated', 'rounds', familyId],
    queryFn: () => api.listFederatedRounds(familyId!),
    enabled: !!familyId,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useKickoffFederatedRound() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ familyId, payload }: { familyId: string; payload: { epsilon?: number; delta?: number } }) =>
      api.kickoffFederatedRound(familyId, payload),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['federated', 'rounds', vars.familyId] });
      queryClient.invalidateQueries({ queryKey: ['privacyBudget'] });
    },
  });
}

export function useFederatedRound(roundId: string | null) {
  return useQuery({
    queryKey: ['federated', 'round', roundId],
    queryFn: () => api.getFederatedRound(roundId!),
    enabled: !!roundId,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useSignFederatedRoundEvidence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roundId: string) => api.signFederatedRoundEvidence(roundId),
    onSuccess: (_d, roundId) => {
      queryClient.invalidateQueries({ queryKey: ['federated', 'round', roundId] });
      queryClient.invalidateQueries({ queryKey: ['federated', 'rounds'] });
    },
  });
}

export function useSubmitFederatedClientUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      roundId,
      payload,
    }: {
      roundId: string;
      payload: API.FederatedClientUpdateInput;
    }) => api.submitFederatedClientUpdate(roundId, payload),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['federated', 'round', vars.roundId] });
      queryClient.invalidateQueries({ queryKey: ['federated', 'rounds'] });
    },
  });
}

export function useRunFederatedAggregation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roundId: string) => api.runFederatedAggregation(roundId),
    onSuccess: (_d, roundId) => {
      queryClient.invalidateQueries({ queryKey: ['federated', 'round', roundId] });
      queryClient.invalidateQueries({ queryKey: ['federated', 'rounds'] });
      queryClient.invalidateQueries({ queryKey: ['privacyBudget'] });
    },
  });
}

// ── Phase 3 — Privacy Budget ────────────────────────────────────────
export function usePrivacyBudget() {
  return useQuery({
    queryKey: ['privacyBudget'],
    queryFn: () => api.getPrivacyBudget(),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useUpdatePrivacyBudgetCaps() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { epsilonCap?: number; deltaCap?: number; windowDurationMs?: number }) =>
      api.updatePrivacyBudgetCaps(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['privacyBudget'] }),
  });
}

// ── Phase 3 — MCP Server ────────────────────────────────────────────
export function useMCPManifest() {
  return useQuery({
    queryKey: ['mcp', 'manifest'],
    queryFn: () => api.getMCPManifest(),
    staleTime: 60 * 60 * 1000,
  });
}

export function useReadMCPResource() {
  return useMutation({
    mutationFn: (uri: string) => api.readMCPResource(uri),
  });
}

export function useCallMCPTool() {
  return useMutation({
    mutationFn: ({ name, args }: { name: string; args: Record<string, any> }) => api.callMCPTool(name, args),
  });
}

// ── Phase 2b — Provenance (C2PA + PROV-O) ──────────────────────────
export function useProvenanceSettings() {
  return useQuery({
    queryKey: ['provenance', 'settings'],
    queryFn: () => api.getProvenanceSettings(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateProvenanceSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { c2paEnabled: boolean }) => api.updateProvenanceSettings(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provenance'] }),
  });
}

export function useC2PAManifests(params?: { limit?: number; traceId?: string }) {
  return useQuery({
    queryKey: ['provenance', 'c2pa', 'manifests', params ?? {}],
    queryFn: () => api.listC2PAManifests(params),
    staleTime: 30 * 1000,
  });
}

export function useIssueC2PAManifest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { traceId: string; contentHash: string; label?: string; issuer?: string; assertions?: Record<string, any> }) =>
      api.issueC2PAManifest(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provenance', 'c2pa'] }),
  });
}

export function useRevokeC2PAManifest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => api.revokeC2PAManifest(id, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provenance', 'c2pa'] }),
  });
}

export function useVerifyC2PAManifest() {
  return useMutation({
    mutationFn: (id: string) => api.verifyC2PAManifest(id),
  });
}

export function useFetchProvOTrace() {
  return useMutation({
    mutationFn: (traceId: string) => api.getProvOTrace(traceId),
  });
}

// ── C2PA KMS — admin signing-key management ───────────────────────────
export function useC2PAKmsStatus() {
  return useQuery({
    queryKey: ['c2pa-kms', 'status'],
    queryFn: () => api.getC2PAKmsStatus(),
    staleTime: 30 * 1000,
  });
}

export function useRotateC2PAKmsKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.rotateC2PAKmsKey(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['c2pa-kms'] }),
  });
}

// ============================================================================
// AUDIT TRAIL — Compliance section hooks. Wrap the raw audit-trail endpoints
// in React Query so loading/error/refetch state is consistent with the rest
// of the dashboard. Polling intervals reflect data freshness:
//   - Witness consortium status: 30s (live observation health)
//   - Audit-chain snapshot: 60s (live chain state)
//   - Notary attestations + Insurance feeds: no polling (monthly cadence)
// ============================================================================

export function useWitnessConsortium() {
  return useQuery({
    queryKey: ['witness-consortium'] as const,
    queryFn: () => api.getWitnessConsortiumStatus(),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useInsuranceFeeds() {
  return useQuery({
    queryKey: ['insurance-feeds'] as const,
    queryFn: () => api.listInsuranceFeeds(),
    staleTime: 5 * 60_000,
  });
}

export function useNotaryAttestations() {
  return useQuery({
    queryKey: ['notary-attestations'] as const,
    queryFn: () => api.listNotaryAttestations(),
    staleTime: 5 * 60_000,
  });
}

export function useAuditorView() {
  return useQuery({
    queryKey: ['policy-auditor-view'] as const,
    queryFn: () => api.getAuditorView(),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useGenerateNotaryAttestation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      periodStart: string;
      periodEnd: string;
      cadence?: 'monthly' | 'biweekly' | 'on-demand';
    }) => api.generateNotaryAttestation(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notary-attestations'] });
    },
  });
}

export function useWitnesses() {
  return useQuery({
    queryKey: ['witnesses'] as const,
    queryFn: () => api.listWitnesses(),
    staleTime: 60_000,
  });
}

export function useRegisterWitness() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      contactEmail: string;
      organisation: string | null;
      country: string;
    }) => api.registerWitness(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['witnesses'] }),
  });
}

export function useDeactivateWitness() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deactivateWitness(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['witnesses'] }),
  });
}

export function useErasureStatus() {
  return useQuery({
    queryKey: ['erasure-status'] as const,
    queryFn: () => api.getErasureStatus(),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useVerifyErasureLedger() {
  return useMutation({
    mutationFn: () => api.verifyErasureLedger(),
  });
}

export function useErasureForTrace() {
  return useMutation({
    mutationFn: (traceId: string) => api.getErasureForTrace(traceId),
  });
}

export function useExecuteErasure() {
  // No invalidation: erasure pages own their result state inline so the
  // success card renders the very response that was returned (chain
  // metadata not needed elsewhere in the dashboard).
  return useMutation({
    mutationFn: (input: {
      traceIds: string[];
      acknowledgment: API.ErasureLedgerAcknowledgment;
      reason: string;
    }) => api.executeErasure(input),
  });
}

export function useGeneratePortableBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { periodStart: string; periodEnd: string; reason?: string }) =>
      api.generatePortableBundle(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portableBundles'] });
    },
  });
}

export function usePortableBundles() {
  return useQuery({
    queryKey: ['portableBundles'],
    queryFn: () => api.listPortableBundles(),
    staleTime: 30_000,
  });
}

export function useAuditChainSnapshot() {
  return useQuery({
    queryKey: ['audit-chain-snapshot'] as const,
    queryFn: () => api.getAuditChainSnapshot(),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

// Compliance Council F-30-C02 (2026-05-21): Heartbeat-History endpoint
// was orphan (no UI). Hook + AuditChainIntegrityPage Timeline section
// added so customer can audit past chain verifications themselves.
export function useHeartbeatHistory(limit: number = 30) {
  return useQuery({
    queryKey: ['heartbeat-history', limit] as const,
    queryFn: () => api.getHeartbeatHistory(limit),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

// ============================================================================
// COMPLIANCE MODULES (11 Modules)
// ============================================================================

// 1. CONSENT
export function useConsents(params?: { purpose?: string; limit?: number }) {
  return useQuery({
    queryKey: ['consents', params],
    queryFn: () => api.listConsents(params),
    staleTime: 30_000,
  });
}

export function useConsent(subjectRef: string | null) {
  return useQuery({
    queryKey: ['consent', subjectRef],
    queryFn: () => api.getConsent(subjectRef!),
    enabled: !!subjectRef,
    staleTime: 30_000,
  });
}

export function useCreateConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subjectRef, purposes }: { subjectRef: string; purposes: any[] }) =>
      api.createConsent(subjectRef, purposes),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consents'] }),
  });
}

export function useWithdrawConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subjectRef, purpose }: { subjectRef: string; purpose: string }) =>
      api.withdrawConsent(subjectRef, purpose),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consents'] }),
  });
}

// 2. DSAR
export function useDsars(params?: { status?: string; subjectRef?: string; limit?: number }) {
  return useQuery({
    queryKey: ['dsars', params],
    queryFn: () => api.listDsars(params),
    staleTime: 30_000,
  });
}

export function useDsar(id: string | null) {
  return useQuery({
    queryKey: ['dsar', id],
    queryFn: () => api.getDsar(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateDsar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createDsar(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dsars'] }),
  });
}

export function useUpdateDsar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updateDsar(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dsars'] }),
  });
}

export function useTransitionDsarStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, ...data }: { id: string; status: string; [key: string]: any }) =>
      api.transitionDsarStatus(id, status, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dsars'] }),
  });
}

export function useExtendDsarDeadline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) => api.extendDsarDeadline(id, notes),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dsars'] }),
  });
}

export function useDeleteDsar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteDsar(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dsars'] }),
  });
}

// 3. SUB-PROCESSOR
export function useSubProcessors(params?: { status?: string; category?: string; limit?: number }) {
  return useQuery({
    queryKey: ['subprocessors', params],
    queryFn: () => api.listSubProcessors(params),
    staleTime: 30_000,
  });
}

export function useSubProcessor(id: string | null) {
  return useQuery({
    queryKey: ['subprocessor', id],
    queryFn: () => api.getSubProcessor(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateSubProcessor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createSubProcessor(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subprocessors'] }),
  });
}

export function useUpdateSubProcessor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updateSubProcessor(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subprocessors'] }),
  });
}

export function useObjectSubProcessor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.objectSubProcessor(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subprocessors'] }),
  });
}

export function useResolveSubProcessorObjection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) => api.resolveSubProcessorObjection(id, approve),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subprocessors'] }),
  });
}

// Art. 28 signed DPA + Art. 44-49 data residency.
export function useCurrentDpa() {
  return useQuery({ queryKey: ['signedDpaCurrent'] as const, queryFn: () => api.getCurrentDpa(), staleTime: 60_000 });
}

export function useDpas() {
  return useQuery({ queryKey: ['signedDpaList'] as const, queryFn: () => api.listDpas(), staleTime: 60_000 });
}

export function useDpaReacceptanceStatus() {
  return useQuery({ queryKey: ['signedDpaReacceptance'] as const, queryFn: () => api.getDpaReacceptanceStatus(), staleTime: 60_000 });
}

export function useAcceptCurrentDpa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { signedByName?: string; signedByEmail?: string }) => api.acceptCurrentDpa(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signedDpaCurrent'] });
      qc.invalidateQueries({ queryKey: ['signedDpaList'] });
      qc.invalidateQueries({ queryKey: ['signedDpaReacceptance'] });
    },
  });
}

export function useDataResidency() {
  return useQuery({ queryKey: ['dataResidency'] as const, queryFn: () => api.getDataResidency(), staleTime: 60_000 });
}

// Art. 32 TOM register + Art. 32(1)(d) effectiveness testing.
export function useTomRegister() {
  return useQuery({ queryKey: ['tomRegister'] as const, queryFn: () => api.getTomRegister(), staleTime: 60_000 });
}

export function useTomEffectiveness(params?: { kind?: string; limit?: number }) {
  return useQuery({ queryKey: ['tomEffectiveness', params] as const, queryFn: () => api.listTomEffectiveness(params), staleTime: 30_000 });
}

export function useRunTomSelfTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.runTomSelfTest(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tomEffectiveness'] });
      qc.invalidateQueries({ queryKey: ['tomRegister'] });
    },
  });
}

export function useRecordTomManualTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { kind: string; overallStatus: string; evidenceUrl?: string; notes?: string }) => api.recordTomManualTest(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tomEffectiveness'] }),
  });
}

// § 25 TDDDG cookie-consent evidence record.
export function useCookieConsents(params?: { subjectRef?: string; withdrawn?: string; limit?: number }) {
  return useQuery({ queryKey: ['cookieConsents', params] as const, queryFn: () => api.listCookieConsents(params), staleTime: 30_000 });
}

export function useWithdrawCookieConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subjectRef: string) => api.withdrawCookieConsent(subjectRef),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cookieConsents'] }),
  });
}

export function useTerminateSubProcessor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.terminateSubProcessor(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subprocessors'] }),
  });
}

// 4. DATA ACCESS
export function useDataAccessRequests(params?: { status?: string; requesterType?: string; search?: string; limit?: number }) {
  return useQuery({
    queryKey: ['dataAccessRequests', params],
    queryFn: () => api.listDataAccessRequests(params),
    staleTime: 30_000,
  });
}

export function useDataAccessRequest(id: string | null) {
  return useQuery({
    queryKey: ['dataAccessRequest', id],
    queryFn: () => api.getDataAccessRequest(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateDataAccessRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createDataAccessRequest(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dataAccessRequests'] }),
  });
}

export function useDecideDataAccessRequest() {
  const qc = useQueryClient();
  return useMutation({
    // Backend expects decision: 'approve' | 'deny' (not a status string).
    mutationFn: ({ id, decision, decisionReason }: { id: string; decision: 'approve' | 'deny'; decisionReason?: string }) =>
      api.decideDataAccessRequest(id, decision, decisionReason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dataAccessRequests'] }),
  });
}

export function useRevokeDataAccessRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decisionReason }: { id: string; decisionReason?: string }) =>
      api.revokeDataAccessRequest(id, decisionReason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dataAccessRequests'] }),
  });
}

// 4a. ACCESS JOURNAL (EU Data Act "Zugänglichkeits-Log" — Art. 4–5 / FiDA Art. 8)
export function useDataAccessJournal(params?: { action?: string; dateFrom?: string; dateTo?: string; search?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['dataAccessJournal', params],
    queryFn: () => api.getDataAccessJournal(params),
    staleTime: 30_000,
  });
}

export function useDataAccessJournalSummary() {
  return useQuery({
    queryKey: ['dataAccessJournalSummary'],
    queryFn: () => api.getDataAccessJournalSummary(),
    staleTime: 30_000,
  });
}

// 4b. THIRD-PARTY DATA-SHARING CONSENT (EU Data Act + FiDA) — Auto-Revoke surface.
export function useDataAccessConsents(params?: { status?: string; recipientRef?: string; search?: string; limit?: number }) {
  return useQuery({
    queryKey: ['dataAccessConsents', params],
    queryFn: () => api.listDataAccessConsents(params),
    staleTime: 30_000,
  });
}

export function useCreateDataAccessConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createDataAccessConsent(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dataAccessConsents'] }),
  });
}

export function useWithdrawDataAccessConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, withdrawalReason }: { id: string; withdrawalReason?: string }) =>
      api.withdrawDataAccessConsent(id, withdrawalReason),
    // Withdrawal cascades: the linked approved data-access requests are
    // auto-revoked (sofort wirksam), so refresh BOTH lists.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dataAccessConsents'] });
      qc.invalidateQueries({ queryKey: ['dataAccessRequests'] });
    },
  });
}

// No useDeleteDataAccessConsent: consents are append-only Art. 5/6 evidence —
// revoke via useWithdrawDataAccessConsent (the backend exposes no hard-delete).

// 5. BIAS AUDIT
export function useBiasAudits(params?: { agentId?: string; period?: string; status?: string; limit?: number }) {
  return useQuery({
    queryKey: ['biasAudits', params],
    queryFn: () => api.listBiasAudits(params),
    staleTime: 60_000,
  });
}

export function useBiasAudit(id: string | null) {
  return useQuery({
    queryKey: ['biasAudit', id],
    queryFn: () => api.getBiasAudit(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCreateBiasAudit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createBiasAudit(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['biasAudits'] }),
  });
}

// 6. DATA QUALITY
export function useDataQualityValidations(params?: { limit?: number }) {
  return useQuery({
    queryKey: ['dataQualityValidations', params],
    queryFn: () => api.listDataQualityValidations(params),
    staleTime: 60_000,
  });
}

export function useDataQualityValidation(id: string | null) {
  return useQuery({
    queryKey: ['dataQualityValidation', id],
    queryFn: () => api.getDataQualityValidation(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCreateDataQualityValidation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createDataQualityValidation(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dataQualityValidations'] }),
  });
}

// 7. ART 22
export function useArt22Explanations(params?: { appealStatus?: string; limit?: number }) {
  return useQuery({
    queryKey: ['art22Explanations', params],
    queryFn: () => api.listArt22Explanations(params),
    staleTime: 30_000,
  });
}

export function useArt22Explanation(id: string | null) {
  return useQuery({
    queryKey: ['art22Explanation', id],
    queryFn: () => api.getArt22Explanation(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useArt22ExplanationForTrace(traceId: string | null) {
  return useQuery({
    queryKey: ['art22ExplanationForTrace', traceId],
    queryFn: () => api.getArt22ExplanationForTrace(traceId!),
    enabled: !!traceId,
    staleTime: 30_000,
  });
}

export function useRequestArt22Appeal() {
  const qc = useQueryClient();
  return useMutation({
    // Accepts a bare id OR { id, dataSubjectStatement } so the data subject can
    // submit their point of view (Art. 22(3)/§ 37 BDSG) when contesting.
    mutationFn: (input: string | { id: string; dataSubjectStatement?: string }) => {
      const { id, dataSubjectStatement } =
        typeof input === 'string' ? { id: input, dataSubjectStatement: undefined } : input;
      return api.requestArt22Appeal(id, dataSubjectStatement);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['art22Explanations'] }),
  });
}

export function useReviewArt22Appeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: string; notes?: string }) =>
      api.reviewArt22Appeal(id, status, notes),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['art22Explanations'] }),
  });
}

// 8. KYC
export function useKycDecisions(params?: { status?: string; sorgfaltsstufe?: string; customerRef?: string; limit?: number }) {
  return useQuery({
    queryKey: ['kycDecisions', params],
    queryFn: () => api.listKycDecisions(params),
    staleTime: 30_000,
  });
}

export function useKycDecision(id: string | null) {
  return useQuery({
    queryKey: ['kycDecision', id],
    queryFn: () => api.getKycDecision(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateKycDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createKycDecision(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kycDecisions'] }),
  });
}

export function useUpdateKycDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updateKycDecision(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kycDecisions'] }),
  });
}

export function useVerifyKycDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.verifyKycDecision(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kycDecisions'] }),
  });
}

export function useRejectKycDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => api.rejectKycDecision(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kycDecisions'] }),
  });
}

export function useMarkKycReviewDue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.markKycReviewDue(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kycDecisions'] }),
  });
}

// 9. SUSPICION
export function useSuspicionCases(params?: { investigationStatus?: string; decision?: string; limit?: number }) {
  return useQuery({
    queryKey: ['suspicionCases', params],
    queryFn: () => api.listSuspicionCases(params),
    staleTime: 30_000,
  });
}

export function useSuspicionCase(id: string | null) {
  return useQuery({
    queryKey: ['suspicionCase', id],
    queryFn: () => api.getSuspicionCase(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateSuspicionCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createSuspicionCase(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suspicionCases'] }),
  });
}

export function useUpdateSuspicionCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updateSuspicionCase(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suspicionCases'] }),
  });
}

export function useTransitionSuspicionStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api.transitionSuspicionStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suspicionCases'] }),
  });
}

export function useDecideSuspicionCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, decisionRationale, fiuReferenceNumber }: { id: string; decision: string; decisionRationale?: string; fiuReferenceNumber?: string }) =>
      api.decideSuspicionCase(id, decision, decisionRationale, fiuReferenceNumber),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suspicionCases'] }),
  });
}

// 10. FAIRNESS REMEDIATION
export function useFairnessRemediations(params?: { status?: string; agentId?: string; dimension?: string; limit?: number }) {
  return useQuery({
    queryKey: ['fairnessRemediations', params],
    queryFn: () => api.listFairnessRemediations(params),
    staleTime: 60_000,
  });
}

export function useFairnessRemediation(id: string | null) {
  return useQuery({
    queryKey: ['fairnessRemediation', id],
    queryFn: () => api.getFairnessRemediation(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCreateFairnessRemediation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createFairnessRemediation(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fairnessRemediations'] }),
  });
}

export function useDecideFairnessRemediation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, rejectionReason }: { id: string; status: string; rejectionReason?: string }) =>
      api.decideFairnessRemediation(id, status, rejectionReason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fairnessRemediations'] }),
  });
}

export function useMarkFairnessRemediationImplemented() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.markFairnessRemediationImplemented(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fairnessRemediations'] }),
  });
}

export function useRevertFairnessRemediation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rejectionReason }: { id: string; rejectionReason?: string }) =>
      api.revertFairnessRemediation(id, rejectionReason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fairnessRemediations'] }),
  });
}

// 11. AUTOMATED DECISION
export function useAutomatedDecisions(params?: { status?: string; decisionType?: string; limit?: number }) {
  return useQuery({
    queryKey: ['automatedDecisions', params],
    queryFn: () => api.listAutomatedDecisions(params),
    staleTime: 30_000,
  });
}

export function useAutomatedDecision(id: string | null) {
  return useQuery({
    queryKey: ['automatedDecision', id],
    queryFn: () => api.getAutomatedDecision(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateAutomatedDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.createAutomatedDecision(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automatedDecisions'] }),
  });
}

export function useUpdateAutomatedDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updateAutomatedDecision(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automatedDecisions'] }),
  });
}

export function useRetireAutomatedDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => api.retireAutomatedDecision(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automatedDecisions'] }),
  });
}
